import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { syncQueryParams } from '@tourfold/libraries/shared-util';
import {
  FolderBrowserComponent,
  type SmartListKind,
} from '../folder-browser/folder-browser.component';
import { provideDocumentManagementPageAiPageContext } from './document-management-page.ai-page-context';

const SMART_VIEWS = ['recent', 'starred', 'deleted'] as const;

function readView(value: string | null): SmartListKind | null {
  return (SMART_VIEWS as readonly string[]).includes(value ?? '') ? (value as SmartListKind) : null;
}

/**
 * Global `/app/documents` and `/app/documents/:folderId` page. Hosts the Miller-column folder
 * browser and syncs the currently focused folder id to the route's `:folderId` segment, plus the
 * currently selected document id to a `?doc=<uuid>` query param. Both are read once on init so
 * a reload (or a shared link) restores the view.
 */
@Component({
  selector: 'tf-document-management-page',
  imports: [FolderBrowserComponent],
  templateUrl: './document-management-page.component.html',
  styleUrl: './document-management-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentManagementPageComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /**
   * Reference to the embedded `<tf-folder-browser>` instance so the AI page-context factory can
   * read the user's current selection, breadcrumbs, and active smart view without having to
   * duplicate the page's template.
   */
  readonly folderBrowser = viewChild(FolderBrowserComponent);

  /** Read once on init from `:folderId`; the browser then drives navigation. */
  protected readonly initialFolderId = signal<string | null>(
    this.route.snapshot.paramMap.get('folderId')
  );

  /** Read once on init from `?doc=`; the browser drives URL updates after that. */
  protected readonly initialDocumentId = signal<string | null>(
    this.route.snapshot.queryParamMap.get('doc')
  );

  /** Read once on init from `?view=` (recent | starred); the browser drives URL updates after. */
  protected readonly initialView = signal<SmartListKind | null>(
    readView(this.route.snapshot.queryParamMap.get('view'))
  );

  constructor() {
    provideDocumentManagementPageAiPageContext({ folderBrowser: this.folderBrowser });

    // Keep `initialFolderId` aligned with the URL so external navigation (back/forward, paste)
    // re-seeds the browser. The browser only consumes this on init; re-emitting via signals
    // triggers its `effect` to walk the new trail.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.initialFolderId.set(params.get('folderId'));
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.initialDocumentId.set(params.get('doc'));
      this.initialView.set(readView(params.get('view')));
    });
  }

  /**
   * Folder-drill click. Drops `?doc=` and `?view=` atomically alongside the path change so all
   * three URL parts settle in one `router.navigate`. Three separate calls would race — each
   * `merge` snapshots the URL before earlier ones applied, so a later `?view=null` would clobber
   * a path change queued before it (the URL ends up at `/app/documents` with no folder id).
   */
  protected onCurrentFolderIdChange(folderId: string | null): void {
    const target = folderId ? ['/app/documents', folderId] : ['/app/documents'];
    void this.router.navigate(target, {
      queryParams: { doc: null, view: null },
      queryParamsHandling: 'merge',
    });
  }

  protected onCurrentDocumentIdChange(documentId: string | null): void {
    syncQueryParams(this.router, this.route, { doc: documentId ?? undefined });
  }

  protected onCurrentViewChange(view: SmartListKind | null): void {
    if (view) {
      // Smart-view click: do path reset (drop `:folderId`) + clear `?doc=` + set `?view=` in one
      // navigate. Three separate router calls race because each `merge` snapshots the URL
      // before earlier queued ones have applied — `?doc=` survives.
      void this.router.navigate(['/app/documents'], {
        queryParams: { view, doc: null },
        queryParamsHandling: 'merge',
      });
    } else {
      syncQueryParams(this.router, this.route, { view: undefined });
    }
  }
}
